import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";

export type TenantCurrencyConversionType = {
  id: string;
  baseCurrency: string;
  conversionCurrency: string;
  /** Fixed rate; in live mode the last live rate fetched, kept as a fallback. */
  rate: number;
  /** When true quotes use the market rate instead of `rate`. */
  useLiveRate: boolean;
  /** Base-currency amount subtracted from the rate so the conversion carries a margin. */
  rateBuffer: number;
  /** Current market rate for live conversions; null when unavailable or fixed. */
  liveRate: number | null;
  /** Rate quotes use (fixed or live, minus the buffer). Null when no positive rate remains. */
  effectiveRate: number | null;
  createdAt: string;
  updatedAt: string;
};

export type CreateTenantCurrencyConversionPayload = Pick<TenantCurrencyConversionType, "baseCurrency" | "conversionCurrency"> &
  Partial<Pick<TenantCurrencyConversionType, "rate" | "useLiveRate" | "rateBuffer">>;

export type UpdateTenantCurrencyConversionPayload = { id: string } & Partial<
  Pick<TenantCurrencyConversionType, "rate" | "useLiveRate" | "rateBuffer">
>;

const currencyConversionQueryKey = (tenantId?: string) =>
  tenantId ? ["tenant-currency-conversions", tenantId] : ["tenant-currency-conversions"];

/** Fetches conversion rates available to the authenticated tenant's quotes. */
export const useTenantCurrencyConversionsQuery = (tenantId?: string, enabled = true) =>
  useQuery({
    queryKey: currencyConversionQueryKey(tenantId),
    queryFn: async (): Promise<TenantCurrencyConversionType[]> => {
      const response = await axios.get("/api/tenants/currency-conversions");
      return response.data.conversions || [];
    },
    staleTime: 0,
    enabled: Boolean(tenantId) && enabled,
  });

/** Creates a new tenant-owned quote conversion rate. */
export const useCreateTenantCurrencyConversion = (tenantId?: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: CreateTenantCurrencyConversionPayload): Promise<TenantCurrencyConversionType> => {
      const response = await axios.post("/api/tenants/currency-conversions", payload);
      return response.data.conversion;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: currencyConversionQueryKey(tenantId) }),
  });
};

/** Updates the rate, live-rate mode or buffer of an existing tenant conversion. */
export const useUpdateTenantCurrencyConversion = (tenantId?: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...changes }: UpdateTenantCurrencyConversionPayload): Promise<TenantCurrencyConversionType> => {
      const response = await axios.put(`/api/tenants/currency-conversions/${id}`, changes);
      return response.data.conversion;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: currencyConversionQueryKey(tenantId) }),
  });
};

/** Deletes a tenant-owned quote conversion rate. */
export const useDeleteTenantCurrencyConversion = (tenantId?: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string): Promise<void> => {
      await axios.delete(`/api/tenants/currency-conversions/${id}`);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: currencyConversionQueryKey(tenantId) }),
  });
};
